/* Axios adapter that answers every request from the in-browser demo API
   instead of the network. The demo server is a separate lazy chunk, so
   the regular app never downloads it. */
import { AxiosError, AxiosHeaders, type AxiosAdapter, type AxiosResponse } from 'axios';
import type { HttpMethod } from './server/router';

const API_PREFIX = '/api';

/** Path relative to the API base, e.g. '/bookings/list'. */
const toApiPath = (pathname: string): string => {
  const i = pathname.indexOf(`${API_PREFIX}/`);
  return i >= 0 ? pathname.slice(i + API_PREFIX.length) : pathname;
};

const parseBody = (data: unknown): unknown => {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

/* Enough delay that loading states read as a real app, not a flicker. */
const latency = () => new Promise((r) => setTimeout(r, 120 + Math.random() * 180));

export const demoAdapter: AxiosAdapter = async (config) => {
  const base = (config.baseURL ?? '').replace(/\/+$/, '');
  const rawUrl = config.url ?? '';
  const full = /^https?:\/\//.test(rawUrl) ? rawUrl : `${base}/${rawUrl.replace(/^\/+/, '')}`;
  const url = new URL(full, window.location.origin);

  const query = new URLSearchParams(url.search);
  if (config.params) {
    for (const [k, v] of Object.entries(config.params)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) v.forEach((item) => query.append(k, String(item)));
      else query.set(k, String(v));
    }
  }

  const method = (config.method ?? 'get').toUpperCase() as HttpMethod;
  const headers = AxiosHeaders.from(config.headers as any);

  const [{ handle }] = await Promise.all([import('./server'), latency()]);
  const result = await handle(
    method,
    toApiPath(url.pathname),
    query,
    parseBody(config.data),
    headers.get('Authorization'),
  );

  const response: AxiosResponse = {
    data: result.data,
    status: result.status,
    statusText: result.status < 400 ? 'OK' : 'Error',
    headers: {},
    config,
    request: {},
  };

  if (result.status >= 400) {
    const message = (result.data as any)?.message ?? `Request failed with status code ${result.status}`;
    throw new AxiosError(
      message,
      result.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
      config,
      {},
      response,
    );
  }
  return response;
};
